import React, { Suspense, useEffect, useState } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import Layout from './components/layout/Layout';
import Loader from './components/shared/Loader';
import ProtectedRoute from './components/common/ProtectedRoute';
import ScrollToTop from './components/common/ScrollToTop';
import SplashScreen from './components/ui/SplashScreen';
import EnrollNowSlide from './components/ui/EnrollNowSlide';
import FloatingActions from './components/ui/FloatingActions';
const MagneticBackground = React.lazy(() => import('./components/ui/MagneticBackground'));
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { authUtils } from './utils/auth.js';
import './styles/themes.css';
import './styles/App.css';

const Home = React.lazy(() => import('./pages/Home'));
const Faculty = React.lazy(() => import('./pages/Faculty'));
const Courses = React.lazy(() => import('./pages/Courses'));
const Homeschooling = React.lazy(() => import('./pages/Homeschooling'));
const CourseDetail = React.lazy(() => import('./pages/CourseDetail'));
const CounselingBook = React.lazy(() => import('./pages/CounselingBook'));
const Counseling = React.lazy(() => import('./pages/Counseling'));
const About = React.lazy(() => import('./pages/About'));
const Contact = React.lazy(() => import('./pages/Contact'));
const Articles = React.lazy(() => import('./pages/Articles'));
const Blogs = React.lazy(() => import('./pages/Blogs'));
const PreRegister = React.lazy(() => import('./pages/PreRegister'));
const Login = React.lazy(() => import('./pages/Login'));
const Signup = React.lazy(() => import('./pages/Signup'));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage'));
const EmailVerification = React.lazy(() => import('./pages/EmailVerification'));
const RegisterInstructor = React.lazy(() => import('./pages/RegisterInstructor'));
const CourseRegistration = React.lazy(() => import('./pages/CourseRegistration'));
const Auth = React.lazy(() => import('./pages/Auth'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('./pages/TermsOfService'));
const CookiePolicy = React.lazy(() => import('./pages/CookiePolicy'));
const RefundPolicy = React.lazy(() => import('./pages/RefundPolicy'));
const TermsAndConditions = React.lazy(() => import('./pages/TermsAndConditions'));
const SocialLinks = React.lazy(() => import('./pages/SocialLinks'));
const FacultyDetail = React.lazy(() => import('./pages/FacultyDetail'));
const ArticleDetail = React.lazy(() => import('./pages/ArticleDetail'));
const GenerationsDetail = React.lazy(() => import('./pages/GenerationsDetail'));
const CounselingPayment = React.lazy(() => import('./pages/CounselingPayment'));
const PaymentStatus = React.lazy(() => import('./pages/PaymentStatus'));
const UnauthorizedPage = React.lazy(() => import('./pages/Unauthorized'));
const AdminApp = React.lazy(() => import('./admin/App'));
const StudentDashboard = React.lazy(() => import('./pages/StudentDashboard'));
const InstructorDashboard = React.lazy(() => import('./pages/InstructorDashboard'));
const DiscountPopup = React.lazy(() => import('./components/ui/DiscountPopup'));
const NewCoursesPopup = React.lazy(() => import('./components/ui/NewCoursesPopup'));

// Route protection for authentication pages (Login, Register, etc.)
// Redirects authenticated users to appropriate dashboard based on role
const AuthPageRoute = ({ children }) => {
    if (!authUtils.isAuthenticated()) {
        return children;
    }
    
    // Get user role from token
    const tokenData = authUtils.decodeToken();
    let userRole = tokenData?.role || tokenData?.userType;

    // Tolerate temporary unknown role while token decoding is pending
    if (!userRole) {
        return (
            <div className="min-h-[50vh] flex items-center justify-center text-gray-600">
                Preparing your dashboard...
            </div>
        );
    }
    // Map synonyms
    if (String(userRole).toLowerCase() === 'teacher') userRole = 'Instructor';
    
    // Redirect based on role
    if (userRole === 'Student') {
        return <Navigate to="/student" replace />;
    } else if (userRole === 'Instructor') {
        return <Navigate to="/instructor" replace />;
    } else if (userRole === 'Admin') {
        return <Navigate to="/admin" replace />;
    } else {
        // If role is unknown or invalid, hold instead of hard-redirecting to unauthorized
        return (
            <div className="min-h-[50vh] flex items-center justify-center text-gray-600">
                Preparing your dashboard...
            </div>
        );
    }
};
function App() {
  const [loading, setLoading] = useState(true);
  const [showSplash, setShowSplash] = useState(true);
  const [showDiscount, setShowDiscount] = useState(false);
  const [showNewCourses, setShowNewCourses] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 800);
    return () => clearTimeout(t);
  }, []);

  // Disable discount popup on all pages (including homepage)
  useEffect(() => {
    setShowDiscount(false);
  }, [location.pathname]);

  const handleSplashComplete = () => {
    setShowSplash(false);
    import('./components/ui/NewCoursesPopup').then(({ shouldShowNewCoursesPopup }) => {
      if (shouldShowNewCoursesPopup()) {
        setShowNewCourses(true);
      }
    });
  };

  useEffect(() => {
    const handleStart = () => setLoading(true);
    const handleDone = () => setLoading(false);
    window.addEventListener('spa:navigate:start', handleStart);
    window.addEventListener('spa:navigate:done', handleDone);
    return () => {
      window.removeEventListener('spa:navigate:start', handleStart);
      window.removeEventListener('spa:navigate:done', handleDone);
    };
  }, []);

  const isAdminRoute = location.pathname.startsWith('/admin');
  const isStudentRoute = location.pathname.startsWith('/student');
  const isInstructorRoute = location.pathname.startsWith('/instructor');

  // Render admin panel separately without public site layout
  if (isAdminRoute) {
    return (
      <ThemeProvider>
        <AuthProvider>
          <ScrollToTop />
          <div className="App">
            <Loader visible={loading} />
            <Suspense fallback={<Loader visible />}>
              <Routes>
                <Route path="/admin/*" element={
                  <ProtectedRoute allowedRoles="Admin">
                    <AdminApp />
                  </ProtectedRoute>
                } />
              </Routes>
            </Suspense>
          </div>
        </AuthProvider>
      </ThemeProvider>
    );
  }

  // Render student dashboard separately without public site layout
  if (isStudentRoute) {
    return (
      <ThemeProvider>
        <AuthProvider>
          <ScrollToTop />
          <div className="App">
            <Loader visible={loading} />
            <Suspense fallback={<Loader visible />}>
              <Routes>
                <Route path="/student/*" element={
                  <ProtectedRoute allowedRoles={["Student"]}>
                    <StudentDashboard />
                  </ProtectedRoute>
                } />
              </Routes>
            </Suspense>
          </div>
        </AuthProvider>
      </ThemeProvider>
    );
  }

  // Render instructor dashboard separately without public site layout
  if (isInstructorRoute) {
    return (
      <ThemeProvider>
        <AuthProvider>
          <ScrollToTop />
          <div className="App">
            <Loader visible={loading} />
            <Suspense fallback={<Loader visible />}>
              <Routes>
                <Route path="/instructor/*" element={
                  <ProtectedRoute allowedRoles={["Instructor"]}>
                    <InstructorDashboard />
                  </ProtectedRoute>
                } />
              </Routes>
            </Suspense>
          </div>
        </AuthProvider>
      </ThemeProvider>
    );
  }

  // Render public site with layout
  return (
    <ThemeProvider>
      <AuthProvider>
        <ScrollToTop />
        <div className="App relative w-screen overflow-x-hidden">
           <Suspense fallback={null}><MagneticBackground /></Suspense>
           <SplashScreen onComplete={handleSplashComplete} />
           <Loader visible={loading} />

           {isAdminRoute ? (
             <Suspense fallback={<Loader visible />}>
               <Routes>
                 <Route path="/admin/*" element={<AdminApp />} />
               </Routes>
             </Suspense>
           ) : (
             !showSplash && (
               <LayoutWithAuth>
                 <Suspense fallback={<Loader visible />}>
                   <Routes>
                     <Route path="/" element={<Home />} />
                     <Route path="/faculty" element={<Faculty />} />
                     <Route path="/faculty/:id" element={
                       <ProtectedRoute allowedRoles={['Student', 'Instructor', 'Admin']}>
                         <FacultyDetail />
                       </ProtectedRoute>
                     } />
                     <Route path="/courses" element={<Courses />} />
                     <Route path="/course/:slug" element={<CourseDetail />} />
                     <Route path="/homeschooling" element={<Homeschooling />} />
                     <Route path="/counseling/book" element={<CounselingBook />} />
                     <Route path="/counseling/payment" element={<CounselingPayment />} />
                     <Route path="/payment-status" element={<PaymentStatus />} />
                     <Route path="/counseling" element={<Counseling />} />
                     <Route path="/about" element={<About />} />
                     <Route path="/contact" element={<Contact />} />
                     <Route path="/articles" element={<Articles />} />
                     <Route path="/articles/:slug" element={<ArticleDetail />} />
                     <Route path="/blogs" element={<Blogs />} />
                     <Route path="/pre-register" element={<PreRegister />} />
                     <Route path="/generations/:session" element={<GenerationsDetail />} />
                     <Route path="/login" element={
                       <AuthPageRoute>
                         <Login />
                       </AuthPageRoute>
                     } />
                     <Route path="/reset-password" element={<ResetPasswordPage />} />
                     <Route path="/verify-email" element={<EmailVerification />} />
                     <Route path="/signup" element={
                       <AuthPageRoute>
                         <Signup />
                       </AuthPageRoute>
                     } />
                     <Route path="/register-instructor" element={
                       <AuthPageRoute>
                         <RegisterInstructor />
                       </AuthPageRoute>
                     } />
                     <Route path="/course-registration" element={<CourseRegistration />} />
                     <Route path="/auth" element={
                       <AuthPageRoute>
                         <Auth />
                       </AuthPageRoute>
                     } />
                     <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                     <Route path="/terms-of-service" element={<TermsOfService />} />
                     <Route path="/cookie-policy" element={<CookiePolicy />} />
                     <Route path="/refund-policy" element={<RefundPolicy />} />
                     <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                     <Route path="/social-links" element={<SocialLinks />} />
                     <Route path="/unauthorized" element={<UnauthorizedPage />} />
                   </Routes>
                 </Suspense>
               </LayoutWithAuth>
           ))}
           {!showSplash && (
            <>
              <EnrollNowSlide />
              <FloatingActions />
              {showDiscount && (
                <DiscountPopup onClose={() => setShowDiscount(false)} />
              )}
              {showNewCourses && (
                <NewCoursesPopup onClose={() => setShowNewCourses(false)} />
              )}
            </>
          )}
        </div>
      </AuthProvider>
    </ThemeProvider>
  );
}

// Layout wrapper that provides auth context to Layout component
const LayoutWithAuth = ({ children }) => {
  const { user, isAuthenticated, logout } = useAuth();

  return (
    <Layout user={user} isAuthenticated={isAuthenticated} onLogout={logout}>
      {children}
    </Layout>
  );
};

export default App;
